import moment from 'moment'
import { FormInvalidMessage } from '../common'
import {
  ActiveSupporter,
  MetricCompletedConversation,
  TrainedSupporter,
} from '../assets'
import { IRecentNotifications } from '../core/app-header/app-header'
import { decryptToken } from '../core/authentication/request-token/request-token'

export enum Pages {
  HOMEPAGE = 'homepage',
  SUPPORTPAGE = 'supportpage',
  ADMINPAGE = 'adminpage',
  PEERSUPPORTERLEAD = 'peersupporterlead',
}

export enum Roles {
  USER = 'user',
  SUPPORTER = 'supporter',
  ADMIN = 'admin',
  PEER_SUPPORTER_LEAD = 'peer_supporter_lead',
  ADMIN_LEAD = 'admin peer_supporter_lead',
  ADMIN_SUPPORTER = 'admin supporter',
  LEAD_SUPPORTER = 'peer_supporter_lead supporter',
  ALL = 'admin peer_supporter_lead supporter',
}

export const RoleBasedPageMapping = {
  homepage: [
    Roles.SUPPORTER,
    Roles.PEER_SUPPORTER_LEAD,
    Roles.ADMIN,
    Roles.ADMIN_SUPPORTER,
    Roles.ADMIN_LEAD,
    Roles.LEAD_SUPPORTER,
    Roles.ALL,
  ],
  supporterpage: [
    Roles.SUPPORTER,
    Roles.ADMIN_SUPPORTER,
    Roles.LEAD_SUPPORTER,
    Roles.ALL,
  ],
  peersupporterleadpage: [
    Roles.PEER_SUPPORTER_LEAD,
    Roles.ADMIN_LEAD,
    Roles.LEAD_SUPPORTER,
    Roles.ADMIN,
    Roles.ADMIN_SUPPORTER,
    Roles.ALL,
  ],
  adminpage: [Roles.ADMIN, Roles.ADMIN_SUPPORTER, Roles.ADMIN_LEAD, Roles.ALL],
  profile: [
    Roles.SUPPORTER,
    Roles.ADMIN,
    Roles.ADMIN_SUPPORTER,
    Roles.PEER_SUPPORTER_LEAD,
    Roles.ADMIN_LEAD,
    Roles.LEAD_SUPPORTER,
    Roles.ALL,
  ],
  notifications: [
    Roles.SUPPORTER,
    Roles.ADMIN_SUPPORTER,
    Roles.PEER_SUPPORTER_LEAD,
    Roles.ADMIN_LEAD,
    Roles.LEAD_SUPPORTER,
  ],
}

export const TrainingStatusOptions = [
  { id: '1', code: 'Certified', name: 'Certified', data: 'Certified' },
  { id: '2', code: 'Uncertified', name: 'Uncertified', data: 'Uncertified' },
]

export const SupporterStatusOptions = [
  { id: '1', code: 'Available', name: 'Active', data: 'Active' },
  { id: '2', code: 'Unavailable', name: 'Inactive', data: 'Inactive' },
]

export const getCreatedDateTime = (date: string): string => {
  const dateAndTime = date.split(',')
  const formattedTime = moment(dateAndTime[1], 'hh:mm:ss A').format('hh:mm A')
  return dateAndTime[0].concat(' ', formattedTime)
}

export const transformRequestResponseData = (responseData: any) => {
  let meetingSlotData = ''
  const hasWeekday = responseData?.meetingSlots.some(
    (slot: any) => slot.dayType.name === 'Weekday'
  )
  const hasWeekend = responseData?.meetingSlots.some(
    (slot: any) => slot.dayType.name === 'Weekend'
  )

  if (hasWeekday && hasWeekend) {
    meetingSlotData = 'Weekday, Weekend'
  } else if (hasWeekday) {
    meetingSlotData = 'Weekday'
  } else if (hasWeekend) {
    meetingSlotData = 'Weekend'
  }
  return {
    id: responseData?.id,
    name: responseData?.referredPeerName || '',
    language: responseData?.language.name || '',
    location: responseData?.referredPeerLocation.name || '',
    email: responseData?.referredPeerEmailId,
    gender: responseData?.gender.name || '',
    roleType: responseData?.referredPeerRoleType.name || '',
    requestType: responseData?.requestType.name || '',
    meetingSlot: meetingSlotData,
    createdDateTime:
      formatDateForSupporterFlow(responseData?.createdAt).toLocaleString() ||
      '',
    meetingPreferences: responseData?.meetingPreference.name || '',
    status: responseData?.status,
  }
}

export const formatDateForSupporterFlow = (dateStr: string): string => {
  // Parse the date string
  const date = new Date(dateStr)

  // Define an array of month names
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ]

  // Get the day, month, and hour
  const day = date.getDate()
  const month = months[date.getMonth()]
  const hour = date.getUTCHours() % 12 || 12
  const meridian = date.getUTCHours() < 12 ? 'AM' : 'PM'

  // Determine the ordinal suffix
  const ordinal = (day: number) => {
    const j = day % 10,
      k = day % 100
    if (j == 1 && k != 11) {
      return day + 'st'
    }
    if (j == 2 && k != 12) {
      return day + 'nd'
    }
    if (j == 3 && k != 13) {
      return day + 'rd'
    }
    return day + 'th'
  }
  return `${ordinal(day)} ${month}, ${hour} ${meridian}`
}

export const transformGetPeerSupporterResponseData = (responseData: any) => {
  return {
    id: responseData?.id,
    name: responseData?.name || '',
    language:
      responseData?.language
        .map((languages: { id: string; name: string }) => languages.name)
        .join(', ') || '',
    location: responseData?.location.name || '',
    gender: responseData?.gender.name || '',
    roleType: responseData?.jobRole.name || '',
    conversationCompleted: responseData?.conversationsCompleted,
    trainingStatus: responseData?.isTrained ? 'Certified' : 'Not Certified',
    contact: responseData?.phoneNumber || '',
    availableToConsult: responseData?.status.name || '',
    emailId: responseData.emailId,
    contactPreference: responseData?.contactPreference.name,
    secondaryEmailId: responseData?.secondaryEmailId,
    createdAt: responseData?.createdAt || '',
  }
}

export const transformGetNominationRequestResponseData = (
  responseData: any
) => {
  return {
    name: responseData?.name || '',
    language:
      responseData?.language
        .map((languages: { id: string; name: string }) => languages.name)
        .join(', ') || '',
    location: responseData?.location.name || '',
    gender: responseData?.gender.name || '',
    roleType: responseData?.jobRole.name || '',
    contact: responseData?.phoneNumber || '',
    emailId: responseData.emailId,
    contactPreference: responseData?.contactPreference.name,
    referredBy: responseData?.referredBy || '',
    referredByEmailId: responseData?.referredByEmailId || '',
    typeOfRequest: responseData?.typeOfRequest.name,
    nominationStatus: responseData?.nominationStatus.name,
    createdAt: responseData?.createdAt,
  }
}

enum MetricNames {
  TRAINED_SUPPORTERS = 'trainedSupporters',
  ACTIVE_SUPPORTERS = 'activeSupporters',
  COMPLETED_SESSIONS = 'completedSessions',
}

export const adminFlowMenuCards = [
  {
    ConversationMenuItem: 'Trained Supporters',
    StatusIcon: TrainedSupporter,
    metricName: MetricNames.TRAINED_SUPPORTERS,
  },
  {
    ConversationMenuItem: 'Active Supporters',
    StatusIcon: ActiveSupporter,
    metricName: MetricNames.ACTIVE_SUPPORTERS,
  },
  {
    ConversationMenuItem: 'Completed Sessions',
    StatusIcon: MetricCompletedConversation,
    metricName: MetricNames.COMPLETED_SESSIONS,
  },
]

export const checkPhoneNumber = (_cc: any, value: any) => {
  if (`${value}`.length < 10) {
    return Promise.reject(FormInvalidMessage('Phone Number'))
  }
  return Promise.resolve()
}

export const checkName = (_cc: any, value: any) => {
  const validNameRegex = /^[^\d]+$/
  if (!validNameRegex.test(value)) {
    return Promise.reject(FormInvalidMessage('Name'))
  }
  return Promise.resolve()
}

export const checkEmail = (_cc: any, value: any) => {
  if (value === undefined || value === null || value === '') {
    return Promise.resolve()
  }
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (!emailRegex.test(value)) {
    return Promise.reject(FormInvalidMessage('Email ID'))
  }
  return Promise.resolve()
}

export const notificationData: IRecentNotifications[] = [
  {
    id: '1',
    roleType: { id: '1', name: 'registered nurse' },
    location: { id: '1', name: 'seattle' },
    createdAt: '2023-11-08T06:20:59.317Z',
  },
  {
    id: '1',
    roleType: { id: '1', name: 'registered nurse' },
    location: { id: '1', name: 'seattle' },
    createdAt: '2023-11-08T06:20:59.317Z',
  },
  {
    id: '1',
    roleType: { id: '1', name: 'registered nurse' },
    location: { id: '1', name: 'seattle' },
    createdAt: '2023-11-08T06:20:59.317Z',
  },
]

export enum RequestTypes {
  SELF = 'self',
  SOMEONE_ELSE = 'someone',
}

export interface INotificationItems {
  id: string
  peerSupporterOid: string
  createdBy: string
  meetingPreference: IMeetingPreference
  meetingSlots: IMeetingSlot[]
  peerSupporterName: string
  requestType: IRequestType
  status: IRequestStatus
  statusLastUpdatedAt: string
}

export interface INominationNotificationItems {
  emailId: string
  name: string
}

export interface IMeetingSlot {
  id: string
  dayType: string
  slotType: string[]
}

export interface IMeetingPreference {
  id: string
  name: string
}

export interface IRequestType {
  id: string
  name: string
}

export interface IRequestStatus {
  id: string
  name: string
}

export interface IProfileItems {
  peerContactNumber: string
  peerEmail: string
  peerName: string
}

export enum ActivityTypes {
  LOGIN = 'Log In',
  LOGOUT = 'Log Out',
}

export const MinistryGuid = [
  {
    id: '3c3a2c6d-bdfe-49b7-9739-f3a23fd48998',
    name: 'No Preference',
  },
  {
    id: '84c28ef3-228e-44cd-a098-a0c3c3fdc8cd',
    name: 'Inside Ministry/Facility',
  },
  {
    id: 'c9315150-6a02-45df-b1de-0d58b5dc2334',
    name: 'Outside Ministry/Facility',
  },
]

export const inactiveStatus = 'Inactive'

export const decryptIdToken = async (): Promise<string | null> => {
  const encryptedToken = sessionStorage.getItem('idToken')
  if (encryptedToken) {
    return await decryptToken(encryptedToken)
  }
  return null
}

export const acceptedStatus = 'approved'
export const rejectedStatus = 'rejected'

export const surveyOptions = [
  { label: 'Encounter Form Analytics', value: 1 },
  { label: 'Support Declined Form Analytics', value: 2 },
  { label: 'Supporter Peer Feedback Form Analytics', value: 3 },
]

export const analyticsUrls = {
  1: 'https://forms.office.com/Pages/AnalysisPage.aspx?AnalyzerToken=P8yT3IxAqcvhYKShH9qReFZYYjU5k7le&id=hpAxLiaao0aGX2Fb7VdnhpTjEM7c6-FJkUfuC8hPcq5UM1NRMjJCOEFIMUxGMTk4QUZNRDZVTEZGUS4u',
  2: 'https://forms.office.com/Pages/AnalysisPage.aspx?AnalyzerToken=xwQzJv5n1DlgIoy1Cw1pwBElBsS300p9&id=hpAxLiaao0aGX2Fb7VdnhpTjEM7c6-FJkUfuC8hPcq5UN1E4SUtIRFFOWEhIUk5SWkpGRThYVzRWSC4u',
  3: 'https://forms.office.com/Pages/AnalysisPage.aspx?AnalyzerToken=Qc8XJmDtB3RqM9NUH93E4DPvUbNYjdSF&id=hpAxLiaao0aGX2Fb7VdnhpTjEM7c6-FJkUfuC8hPcq5UMURWQlcyV0haRFFMR0pTRTc5T001RUFHOS4u',
}

export const excelUrls = {
  1: 'https://forms.office.com/Pages/DesignPageV2.aspx?subpage=design&token=b82c47bd64cb4843ae35d420a368682b&id=hpAxLiaao0aGX2Fb7VdnhpTjEM7c6-FJkUfuC8hPcq5UM1NRMjJCOEFIMUxGMTk4QUZNRDZVTEZGUS4u&analysis=true&tab=0&topview=SurveyResults&qid=ra18dfa6783f04f8cb4459395e4734d4c&ridx=0',
  2: 'https://forms.office.com/Pages/DesignPageV2.aspx?subpage=design&token=e181fd0f995c44258fa0bd580933de51&id=hpAxLiaao0aGX2Fb7VdnhpTjEM7c6-FJkUfuC8hPcq5UN1E4SUtIRFFOWEhIUk5SWkpGRThYVzRWSC4u&analysis=true&tab=0&topview=SurveyResults&qid=r32b308e083554dfe9d23ebff1de29712&ridx=0',
  3: 'https://forms.office.com/Pages/DesignPageV2.aspx?subpage=design&token=caf822fe18d041a1bf87d3f0e1dc1f22&id=hpAxLiaao0aGX2Fb7VdnhpTjEM7c6-FJkUfuC8hPcq5UMURWQlcyV0haRFFMR0pTRTc5T001RUFHOS4u&analysis=true&topview=SurveyResults&qid=rbed341199a1a424e941bae01ea7ecc07&ridx=0',
}

export const editNotifDescription = {
  1: 'Metadata edited successfully',
  2: 'Failed to edit Metadata',
}

export const DeletePSLNotification = {
  1: 'Peer Supporter Lead deleted successfully',
  2: 'Failed to delete Peer Supporter Lead',
}

export const requestStatuses = {
  1: 'On Hold',
  2: 'In Progress',
  3: 'Request Submitted',
}

export const bulkUploadConstants = {
  1: 'supporters',
  2: 'requests',
}

export const groupMeetingSlots = (
  detailedSlots: {
    id: string
    dayType: { id: string; name: string }
    slotType: { id: string; name: string }
  }[]
): { dayType: string; slotType: string[] }[] => {
  if (!Array.isArray(detailedSlots)) return []

  const slotMap: Record<string, Set<string>> = {}

  detailedSlots.forEach((slot) => {
    const day = slot.dayType.name
    const time = slot.slotType.name

    if (!slotMap[day]) {
      slotMap[day] = new Set()
    }

    slotMap[day].add(time)
  })

  return Object.entries(slotMap).map(([dayType, slotTypeSet]) => ({
    dayType,
    slotType: Array.from(slotTypeSet),
  }))
}

export const requestNotification = 'requestNotification'
export const supportRequestNotification = 'supportRequestNotification'
export const systemRole = 'System Level Role'
export const outsideMinistrySetting = 'Outside Ministry/Facility'
export const noPreference = 'No Preference'

export const MEETING_SLOTS_TOOLTIP = {
  SELF: 'Preferred time slots for connecting with supporters; choose the meeting times that work best for you',
  OTHER:
    'Preferred time slots for connecting with supporters; choose the meeting times that you think might work. The person you are referring can update them',
} as const
